import firebase from '../../firebase/compat';
import { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { db } from '../../firebase/config';
import { client } from '../../hooks/Client';
import { Auth } from '../../StateManagment/Auth';
import ReadOnlyGuard from '../common/ReadOnlyGuard';
import DeleteModal from '../common/DeleteModal';
import Table from '../common/table/Table.jsx';
import TableCell from '../common/table/TableCell.jsx';
import softDeleteDoc from '../../helpers/softDeleteDoc';
import { validateAdjustment } from '../../helpers/billing/validateTariff';
import { formatQuantity, skipReasonLabel, adjustmentBlockerText } from '../../helpers/billing/billingLabels';
import skipFix from '../../helpers/billing/skipFix';

/**
 * @typedef {object} BillingAdjustment
 * @property {string} ID
 * @property {string} docid
 * @property {string} PersonaID
 * @property {string} Period - 'YYYY-MM'
 * @property {number} Quantity - In de eenheid van het traject van de deelnemer
 * @property {string} Reason
 */

/**
 * Link naar de plek waar je oplost waarom een extra regel niet meetelt.
 *
 * @param {object} props
 * @param {string} props.reason - Reden uit `computeBilling`
 * @param {string} props.personaId - `ID` van de deelnemer
 * @returns {JSX.Element|null}
 */
const FixLink = ({ reason, personaId }) => {
    const fix = skipFix(reason, personaId);
    if (!fix) return null;
    return <>{' · '}<Link to={`/${client}/${fix.path}`}>{fix.label}</Link></>;
};

/**
 * Extra regels op de facturatie van één maand: wat een medewerker bewust
 * bijschrijft, zoals een dagdeel bij veel contactmomenten (Het Blok,
 * 2026-10-22). Nooit automatisch; altijd met een reden, en op de factuur apart
 * van de aanwezigheid. Het aantal telt in de eenheid van het traject.
 *
 * AI-CONTEXT: See docs/features/facturatie-per-deelnemer.md
 *
 * @param {object} props
 * @param {string} props.period - De gekozen maand, 'YYYY-MM'
 * @param {Array<{ID: string, Name?: string}>} props.personas - Deelnemers om uit te kiezen (al geladen)
 * @param {Array<BillingAdjustment>} props.adjustments - De extra regels van deze maand (al geladen)
 * @param {Record<string, string>} props.notCounted - Per extra regel (ID) waarom hij niet meetelt (`computeBilling().skippedAdjustments`)
 * @param {(personaId: string) => ({reason: string, detail: string}|null)} props.blockerFor - Waarom een extra regel
 *   voor deze deelnemer niet zou meetellen (`adjustmentBlocker`), of `null`
 * @returns {JSX.Element}
 */
const BillingAdjustments = ({ period, personas, adjustments, notCounted, blockerFor }) => {
    const [auth] = useContext(Auth);
    const [personaId, setPersonaId] = useState('');
    const [quantity, setQuantity] = useState('1');
    const [reason, setReason] = useState('');
    const [errors, setErrors] = useState(/** @type {{personaId?: string, quantity?: string, reason?: string}} */ ({}));
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(false);
    const [deleteError, setDeleteError] = useState(false);
    const [toDelete, setToDelete] = useState(/** @type {BillingAdjustment|null} */ (null));

    const nameById = new Map(personas.map((p) => [p.ID, p.Name || '']));
    const nameOf = (id) => nameById.get(id) || 'Onbekende deelnemer';
    const options = [...personas].sort((a, b) => (a.Name || '').localeCompare(b.Name || '', 'nl'));
    const selectedBlocker = personaId ? blockerFor(personaId) : null;

    const addAdjustment = async () => {
        if (saving) return;
        const result = validateAdjustment({ quantity, reason });
        const nextErrors = { ...result.errors };
        if (!personaId) nextErrors.personaId = 'Kies een deelnemer.';
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) return;

        setSaving(true);
        setSaveError(false);
        try {
            await db.collection('PersonaBillingAdjustments').add({
                ID: uuid(),
                CompagnyID: client,
                Compagny: client,
                PersonaID: personaId,
                Period: period,
                Quantity: result.quantity,
                Reason: result.reason,
                Title: `Extra regel ${period}: ${nameOf(personaId)}`,
                Created: Date.now(),
                CreatedBy: auth?.ID || '',
                Timestamp: firebase.firestore.FieldValue.serverTimestamp(),
                Deleted: false,
            });
            setPersonaId('');
            setQuantity('1');
            setReason('');
        } catch (error) {
            console.error('BillingAdjustments: opslaan mislukt', error);
            setSaveError(true);
        } finally {
            setSaving(false);
        }
    };

    const deleteAdjustment = async () => {
        const adjustment = toDelete;
        setToDelete(null);
        setDeleteError(false);
        try {
            await softDeleteDoc({
                collection: 'PersonaBillingAdjustments',
                docid: adjustment.docid,
                authId: auth?.ID,
                title: `Extra regel ${adjustment.Period}: ${nameOf(adjustment.PersonaID)}`,
            });
        } catch (error) {
            console.error('BillingAdjustments: verwijderen mislukt', error);
            setDeleteError(true);
        }
    };

    return (
        <section className="billing-page__card billing-adjustments" aria-labelledby="billing-adjustments-title">
            <h3 id="billing-adjustments-title">Extra regels</h3>
            <ReadOnlyGuard edit="billing.edit">
                {adjustments.length === 0 ? (
                    <p className="billing-adjustments__empty">Geen extra regels in deze maand.</p>
                ) : (
                    <Table
                        columns={[
                            { key: 'name', label: 'DEELNEMER', searchable: false },
                            { key: 'quantity', label: 'AANTAL', searchable: false },
                            { key: 'reason', label: 'REDEN', searchable: false },
                            { key: 'delete', label: '', searchable: false },
                        ]}
                        items={adjustments}
                        getItemKey={(a) => a.docid}
                        showSearch={false}
                        renderRow={(a) => (
                            <>
                                <TableCell>{nameOf(a.PersonaID)}</TableCell>
                                <TableCell>{formatQuantity(Number(a.Quantity) || 0)}</TableCell>
                                <TableCell>
                                    {a.Reason}
                                    {notCounted[a.ID] && (
                                        <span className="billing-adjustments__not-counted">
                                            Telt niet mee — {skipReasonLabel(notCounted[a.ID]).toLowerCase()}
                                            <FixLink reason={notCounted[a.ID]} personaId={a.PersonaID} />
                                        </span>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <button
                                        type="button"
                                        className="billing-tariffs__delete"
                                        aria-label={`Extra regel van ${nameOf(a.PersonaID)} verwijderen`}
                                        onClick={() => setToDelete(a)}
                                    >
                                        <DeleteOutlinedIcon />
                                    </button>
                                </TableCell>
                            </>
                        )}
                    />
                )}

                <div className="billing-adjustments__form">
                    <label className="billing-tariffs__field">
                        <span>Deelnemer</span>
                        <select value={personaId} onChange={(e) => setPersonaId(e.target.value)}>
                            <option value="">-- Kies een deelnemer --</option>
                            {options.map((p) => <option key={p.ID} value={p.ID}>{p.Name}</option>)}
                        </select>
                        {errors.personaId && <span className="billing-tariffs__field-error">{errors.personaId}</span>}
                    </label>
                    <label className="billing-tariffs__field billing-adjustments__quantity">
                        <span>Aantal</span>
                        <input type="number" min="1" step="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
                        {errors.quantity && <span className="billing-tariffs__field-error">{errors.quantity}</span>}
                    </label>
                    <label className="billing-tariffs__field billing-adjustments__reason">
                        <span>Reden</span>
                        <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} />
                        {errors.reason && <span className="billing-tariffs__field-error">{errors.reason}</span>}
                    </label>
                    <button type="button" className="billing-tariffs__add" onClick={addAdjustment} disabled={saving}>
                        Extra regel toevoegen
                    </button>
                </div>
                {/* Vooraf zeggen wat de factuur straks ook zegt: dezelfde regel (adjustmentBlocker). */}
                {selectedBlocker && (
                    <p className="billing-adjustments__hint">{adjustmentBlockerText(selectedBlocker, nameOf(personaId))}</p>
                )}
                {saveError && <p className="billing-tariffs__error" role="alert">Niet opgeslagen. Probeer het opnieuw.</p>}
                {deleteError && <p className="billing-tariffs__error" role="alert">Niet verwijderd. Probeer het opnieuw.</p>}
            </ReadOnlyGuard>

            <DeleteModal
                deleteModal={!!toDelete}
                setDeleteModal={(open) => { if (!open) setToDelete(null); }}
                deleteItem={deleteAdjustment}
                deleteName={toDelete ? `de extra regel van ${nameOf(toDelete.PersonaID)}` : ''}
                softDelete
            />
        </section>
    );
};

export default BillingAdjustments;
